import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalStorage } from 'usehooks-ts';
import _get from 'lodash/get';
import {
  Task,
  TaskCompletion,
  useUsersSearchListLazyQuery,
} from 'graphql-common';
import { Option, Value, Values } from '@lib/interfaces/form';
import usePaginationParams from '@lib/hooks/usePaginationParams';
import { useTableItemsBulkSelectWithAllIds } from '@lib/hooks/useTableItemsBulkSelect';
import { TASK_REPORTING_FILTER_FORM } from '@lib/enums/localStorageKeys';
import useTaskCompletionsQueryHook from '@lib/utils/fetch/hooks/useTaskCompletionsQueryHook';
import { getUsersLoadOptions as getUsersLoadOptionsFn } from 'utils/fetch/searchLists/usersSearchList';
import TaskCompletionsFilterFieldNames from '@lib/enums/fieldNames/taskCompletionsFilterFieldNames';
import { taskCompletionsModalUrls } from 'components/TaskCompletionsModal/TaskCompletionsModal';
import useTaskCompletionWriteActions from 'hooks/useTaskCompletionWriteActions';
import getTaskCompletionsTableColumns from '@lib/components/TaskCompletions/utils/getTaskCompletionsTableColumns';
import getTaskCompletionsListActionsForSelected from '@lib/components/TaskCompletions/utils/getTaskCompletionsListActionsForSelected';
import useTaskCompletionsSelectionActions from '@lib/components/TaskCompletions/utils/useTaskCompletionsSelectionActions';
import useTaskReportingFilterFields, {
  TASK_REPORTING_FILTER_DEFAULT_VALUES,
} from './useTaskReportingFilterFields';
import TaskCompletionMediaCell from './TaskCompletionMediaCell';
import getTaskCompletionsFilters from './getTaskCompletionsFilters';
import useTaskReportingPeriod from './useTaskReportingPeriod';

export default function useTaskReportingActions({
  task,
  taskId,
}: {
  task?: Task;
  taskId?: string;
}) {
  const { t } = useTranslation();
  const [paginationParams] = usePaginationParams();

  const [selectedCompletionId, setSelectedCompletionId] = useState<
    string | null
  >(null);

  const [filterParams, setFilterParams] = useLocalStorage<{
    [key: string]: Value;
  }>(TASK_REPORTING_FILTER_FORM, TASK_REPORTING_FILTER_DEFAULT_VALUES);

  // Users selector
  const [fetchUsersSearchListLazyQuery] = useUsersSearchListLazyQuery({
    fetchPolicy: 'cache-and-network',
  });
  const getUsersLoadOptions = () =>
    getUsersLoadOptionsFn(fetchUsersSearchListLazyQuery);
  const filterFields = useTaskReportingFilterFields({ getUsersLoadOptions });

  // Period defaults + selected value
  const { defaultMinDateObj, defaultMaxDateObj, periodValue } =
    useTaskReportingPeriod({
      filterParams: filterParams as { period?: string[] },
    });

  // Build query filters and fetch
  const groupKey = task?.groupKey;
  const skipCompletions = !groupKey;
  const additionalFilters = useMemo(
    () => getTaskCompletionsFilters({ filterParams, periodValue }),
    [filterParams, periodValue],
  );

  const {
    collection,
    error: completionsError,
    firstLoading: completionsFirstLoading,
    loading: completionsLoading,
    refetch,
    fetchIds,
  } = useTaskCompletionsQueryHook({
    groupKey: groupKey || '',
    skip: skipCompletions,
    additionalFilters,
    fetchPolicy: 'network-only',
    paginationParams,
  });

  // Bulk select — the table has no pagination (`fetchIds` resolves the same
  // filtered set already in `collection`), so "select all" both materializes
  // ids consistently and keeps the selected-count accurate in every mode.
  const useBulkSelectResult = useTableItemsBulkSelectWithAllIds<TaskCompletion>(
    { fetchIds },
  );

  const { onStatusChange, isStatusUpdating, onCreateInterventionForSelected } =
    useTaskCompletionWriteActions({ task, taskId, onStatusUpdated: refetch });

  const {
    bulkCompareIds,
    setBulkCompareIds,
    onCompareSelected,
    onViewIntervention,
    onCreateIntervention,
    onCreateInterventionBulk,
  } = useTaskCompletionsSelectionActions({
    groupKey,
    collection: collection as TaskCompletion[],
    selectedItems: useBulkSelectResult.selectedTableItems,
    getInterventionUrl: taskCompletionsModalUrls.getInterventionUrl,
    onCreateInterventionForSelected,
  });

  // Filter counters
  let filterCounter = 0;
  if (_get(filterParams, [TaskCompletionsFilterFieldNames.Users, 'length'])) {
    filterCounter += 1;
  }
  if (
    _get(filterParams, [
      TaskCompletionsFilterFieldNames.ValidityHints,
      'length',
    ])
  ) {
    filterCounter += 1;
  }

  // Handlers
  const onResetFilters = () =>
    setFilterParams(TASK_REPORTING_FILTER_DEFAULT_VALUES);

  const onSubmitFilter = (values: Values) => {
    const users =
      (values?.[TaskCompletionsFilterFieldNames.Users] as Option[]) || [];
    const validityHints =
      (values?.[TaskCompletionsFilterFieldNames.ValidityHints] as Option[]) ||
      [];
    setFilterParams((prev) => ({
      ...prev,
      name: (values?.name as string | null) || null,
      [TaskCompletionsFilterFieldNames.Users]: users,
      [TaskCompletionsFilterFieldNames.ValidityHints]: validityHints,
    }));
  };

  const onPeriodChange = (value: string[]) => {
    if (Array.isArray(value) && value[0] && value[1])
      setFilterParams((prev) => ({ ...prev, period: value }));
    else setFilterParams((prev) => ({ ...prev, period: [] }));
  };

  const onViewTaskCompletionHandler = (item?: TaskCompletion) => {
    if (item?.id) setSelectedCompletionId(item.id);
  };

  // One modal serves both the single-completion detail and bulk compare, so
  // closing it clears whichever of the two opened it.
  const onCloseCompletionsModal = () => {
    setSelectedCompletionId(null);
    setBulkCompareIds(null);
    useBulkSelectResult.handleUnselectAllTableItems();
  };

  const tableColumns = useMemo(
    () =>
      getTaskCompletionsTableColumns({
        t,
        onViewCompletion: onViewTaskCompletionHandler,
        onStatusChange,
        isStatusUpdating,
        renderMediaCell: (completion) => (
          <TaskCompletionMediaCell
            completion={completion}
            groupKey={groupKey || ''}
          />
        ),
      }),

    [groupKey, onStatusChange, t, isStatusUpdating],
  );

  const tableActionsForSelected = getTaskCompletionsListActionsForSelected({
    t,
    onCreateInterventionSelected: onCreateInterventionBulk,
    onCompareSelected,
    selectedItemsCount: useBulkSelectResult.selectedTableItems.length,
    canCompare: collection.length > 1,
  });

  return {
    // Filter UI
    filterCounter,
    filterDefaultValues: filterParams,
    filterFields,
    minDate: defaultMinDateObj,
    maxDate: defaultMaxDateObj,
    periodValue,
    onPeriodChange,
    onResetFilters,
    onSubmitFilter,
    onCloseCompletionsModal,
    onViewTaskCompletionHandler,
    onCreateIntervention,
    onViewIntervention,

    // Table UI
    tableColumns,
    tableData: collection as TaskCompletion[],
    tableLoading: completionsLoading || isStatusUpdating,
    tableFirstLoading: completionsFirstLoading,
    tableError: completionsError,

    // Bulk select
    useBulkSelectResult,
    tableActionsForSelected,
    bulkCompareIds,

    selectedCompletionId,
    fetchIds,
  };
}
